import {getTestProps} from '@benchling/js-test-utils';
import {sleep} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useLayoutEffect, useEffect, useRef, useState} from 'react';

import useResizeObserver from 'ui-library/hooks/useResizeObserver';

import {LatestUserMessageInfo} from '../ChatScrollTypes';

// When auto-scrolling to the latest user message, we leave this many pixels of room above that
// user message.
const TOP_MARGIN_PX = 16;
const SCROLLBAR_WIDTH = 8; // keep in sync with variables.less:@scrollbar-width

interface ChatScrollContainerProps {
  autoScroll: boolean;
  chatMessagesElement: React.ReactNode;
  chatInputElement: React.ReactNode;
  latestUserMessageInfo: LatestUserMessageInfo | null;
}

/**
 * Layout component for chat interfaces. The input area is pinned to the bottom of the container
 * and the chat messages are scrollable.
 *
 * This component also implements auto-scrolling as the user sends new messages. Each time a
 * message is sent, we scroll to keep that message that top of the visible space (with some margin)
 * and add whitespace below via min-height.
 *
 * This component currently enforces that all chats have a max width of 800px, though this could
 * be decoupled in the future if necessary. Importantly, the scroll bar should be on the right
 * side of the outer container (not the right side of the messages) when the max-width applies.
 */
export default function ChatScrollContainer({
  autoScroll,
  chatMessagesElement,
  chatInputElement,
  latestUserMessageInfo,
}: ChatScrollContainerProps): JSX.Element {
  const chatThreadScrollContainerElement = useRef<HTMLDivElement | null>(null);
  const chatThreadContentsElement = useRef<HTMLDivElement | null>(null);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  // The chat thread could resize due to window resize, typing multiple lines into the input box,
  // or dragging the chat/document slider. In any case, we may need to recalculate the min-height
  // on change.
  const {width: scrollContainerWidth, height: scrollContainerHeight} = useResizeObserver<HTMLDivElement>({
    ref: chatThreadScrollContainerElement,
  });
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  const lastScrolledIndex = useRef<number>(-1);

  const latestUserMessageElement = latestUserMessageInfo?.element ?? null;
  const latestUserMessageIndex = latestUserMessageInfo?.index ?? null;

  // At any point in time (after the initial scroll), we want it to be possible to scroll the latest
  // user message to the top of the viewable area, so we calculate that space.
  const recalculateMinHeight = useCallback(() => {
    // For the very initial scroll-to-bottom, we want to scroll-to-bottom *without* this extra space
    // below. Use the hasAutoScrolled state to coordinate: we hold off on min-height calculations
    // until it's marked true below, indicating that we've successfully done our initial scroll.
    // It's fine to immediately add the space right after that.
    if (!hasAutoScrolled) {
      return;
    }
    if (
      latestUserMessageElement &&
      // On re-render with a different element, we may get a disconnected bounding ref with top=0.
      // In that case, just wait for the next ref and consider this one invalid.
      latestUserMessageElement.isConnected &&
      chatThreadScrollContainerElement.current &&
      chatThreadContentsElement.current
    ) {
      const lastMessageBoundingRect = latestUserMessageElement.getBoundingClientRect();
      const chatThreadContentsBoundingRect = chatThreadContentsElement.current.getBoundingClientRect();

      const lastMessagePositionPixels =
        lastMessageBoundingRect.top - chatThreadContentsBoundingRect.top - TOP_MARGIN_PX;
      setMinHeight(lastMessagePositionPixels + chatThreadScrollContainerElement.current.clientHeight);
    }
  }, [latestUserMessageElement, hasAutoScrolled]);

  // On initial render and any resize, recalculate the bottom spacing. Use layout effect to ensure that there
  // isn't a delay in updating the min-height. Otherwise, if the user vertically shrunk the window, a
  // scrollbar would appear temporarily. An alternative is to use overflow-y: scroll instead of auto.
  useLayoutEffect(() => {
    recalculateMinHeight();
  }, [recalculateMinHeight, scrollContainerWidth, scrollContainerHeight]);

  // On initial load (after the proper elements come in) and on any new message, automatically
  // scroll to the right place.
  useEffect(() => {
    // Wait until we have an actual element before any auto-scrolling. For example, there may be
    // a loading state, so wait for that to finish.
    if (latestUserMessageIndex == null || latestUserMessageElement == null) {
      return;
    }

    async function scrollToLatestUserMessage(): Promise<void> {
      // Wait for the min-height to be updated, then scroll once we've rendered with the extra space.
      recalculateMinHeight();
      await sleep(1);
      // Just scroll to the bottom of the scrollable area, which is sized to be exactly the right spot
      // so the message is on top with a little margin.
      // Guard against unmount during the sleep - the ref may be null if component unmounted,
      // or scrollTo may be unavailable in some environments (e.g., during test cleanup).
      chatThreadScrollContainerElement.current?.scrollTo?.({
        top: chatThreadScrollContainerElement.current.scrollHeight,
        behavior: 'smooth',
      });
    }

    if (!hasAutoScrolled && autoScroll) {
      // Initial scroll: before we do any artificial min-height, scroll to the bottom of the
      // scrolllable area.
      chatThreadScrollContainerElement.current!.scrollTo({
        top: chatThreadScrollContainerElement.current!.scrollHeight,
        behavior: 'instant',
      });
      lastScrolledIndex.current = latestUserMessageIndex;
      setHasAutoScrolled(true);
    } else if (latestUserMessageIndex > lastScrolledIndex.current) {
      // Scroll on new message: recalculate the min-height, render, then scroll to the bottom.
      lastScrolledIndex.current = latestUserMessageIndex;
      scrollToLatestUserMessage();
    }
  }, [latestUserMessageIndex, latestUserMessageElement, recalculateMinHeight, hasAutoScrolled, autoScroll]);

  return (
    <div
      className={css(styles.chatScrollContainer)}
      {...getTestProps({component: 'ChatScrollContainer', element: 'root'})}
    >
      <div
        ref={chatThreadScrollContainerElement}
        className={classNames(css(styles.chatThreadScrollContainer), 'scrollbar-override')}
      >
        <div
          ref={chatThreadContentsElement}
          className={css(styles.chatThreadContainer)}
          style={minHeight ? {minHeight: `${minHeight}px`} : undefined}
        >
          {chatMessagesElement}
        </div>
      </div>
      {chatInputElement && (
        <div className={css(styles.chatInputContainer)}>
          <div className={css(styles.chatInputContainerFade)} />
          {chatInputElement}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  chatScrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 0,
    flex: 1,
    width: '100%',
  },
  // The scrolling portion of the chat thread includes the blank space to the left and right
  // of the chat messages, so it needs overflowY without the maxWidth set yet.
  chatThreadScrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    overflowY: 'auto',
    flex: 1,
  },
  chatThreadContainer: {
    width: '100%',
    maxWidth: '800px',
  },
  chatInputContainer: {
    width: '100%',
    maxWidth: '800px',
    padding: 16,
    paddingTop: 0,
    paddingBottom: 0,
    position: 'relative',
  },
  chatInputContainerFade: {
    position: 'absolute',
    top: -16,
    height: 16,
    left: 0,
    right: SCROLLBAR_WIDTH,
    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
  },
});
